import React from 'react';
import { graphql } from 'gatsby';
import Layout from 'components/Layout';
import SEO from 'components/SEO';
import Hero from 'components/Hero';
import HomepagePitch from 'components/_page/homepage/Pitch';
import EventsWebinars from 'components/_page/homepage/EventsWebinars';
import FeaturedCaseStudies from 'components/FeaturedCaseStudies';
import Partners from 'components/Partners';
import ContentList from 'components/ContentList.jsx';
import FeatureProducts from 'components/FeatureProducts';
import FeatureSolutions from 'components/FeatureSolutions';
import Trapdoor from 'components/Trapdoor';
import FeaturedResources from 'components/FeaturedResources';

export default ({ data }) => {
  const homepage = data.prismic.allHomepages.edges[0].node;

  return (
    <Layout>
      <SEO
        title={homepage.social_title || 'Home'}
        description={homepage.social_description}
        image={homepage.social_image ? homepage.social_image.url : null}
      />

      <Hero
        heading={homepage.hero_heading}
        content={homepage.hero_content}
        image={homepage.hero_image}
        illustration={homepage.hero_illustration}
        buttons={[
          {
            text: homepage.hero_button_text || 'Get Started',
            destination: homepage.hero_button_destination || '/pricing',
          },
          {
            text: homepage.hero_video_button_text || 'Watch the Video',
            destination: '#watch',
            style: 'secondary',
            icon: 'video',
            iconPosition: 'left',
          },
        ]}
        size='large'
        imagePosition="right"
      />

      <HomepagePitch
        heading={homepage.pitch_heading}
        content={homepage.pitch_content}
        buttonDestination={homepage.pitch_button_destination}
        buttonText={homepage.pitch_button_text}
        widget={homepage.pitch_widget_name || '0011r00002QWpLv_1626'}
        caption={homepage.pitch_caption}
        videoPosition="right"
      />

      {(homepage.results_list && homepage.results_list.length > 0) && (
        <ContentList
          intro={homepage.results_intro}
          heading={homepage.results_heading}
          content={homepage.results_content}
          items={homepage.results_list}
          variant='homepage'
        />
      )}

      {(homepage.products_list && homepage.products_list.length > 0) && (
        <FeatureProducts
          intro={homepage.products_intro}
          heading={homepage.products_heading}
          products={homepage.products_list}
          variant="homepage"
        />
      )}

      {(homepage.solutions && homepage.solutions.length > 0) && (
      <FeatureSolutions
        intro={homepage.solutions_intro}
        heading={homepage.solutions_heading}
        solutions={homepage.solutions}
        variant="homepage"
      />
      )}

      {homepage.trapdoor_destination && (
        <Trapdoor
          heading={homepage.trapdoor_heading}
          buttonText={homepage.trapdoor_button_text}
          destination={homepage.trapdoor_destination}
        />
      )}

      {(homepage.case_study_list && homepage.case_study_list.length > 0) &&
      <FeaturedCaseStudies
        delay={homepage.case_study_delay || 6000}
        slides={homepage.case_study_list}
        intro={homepage.partners_intro}
        heading={homepage.partners_heading}
      />
      }

      {(homepage.latest_partners && homepage.latest_partners.length > 0) && (
        <Partners
          partners={homepage.latest_partners}
          variant='homepage'
        />
      )}

      {(homepage.events_slides && homepage.events_slides.length > 0) && (
        <EventsWebinars
          intro={homepage.events_intro}
          heading={homepage.events_heading}
          slides={homepage.events_slides}
          delay={homepage.events_slider_delay || 6000}
        />
      )}

      <FeaturedResources
        caption={homepage.resources_caption}
        heading={homepage.resources_heading}
        intro={homepage.resources_intro}
        widget={homepage.resources_widget_name}
      />
    </Layout>
  );
};

export const query = graphql`
    {
        prismic {
            allHomepages {
                edges {
                    node {
                        hero_heading
                        hero_content
                        hero_image
                        hero_illustration {
                            ... on PRISMIC__FileLink {
                                _linkType
                                name
                                size
                                url
                            }
                        }
                        hero_button_text
                        hero_button_destination
                        hero_video_button_text
                        pitch_button_destination
                        pitch_button_text
                        pitch_widget_name
                        pitch_caption
                        pitch_content
                        pitch_heading
                        results_intro
                        results_heading
                        results_content
                        results_list {
                            heading1
                            image
                            illustration {
                                ... on PRISMIC__FileLink {
                                    _linkType
                                    name
                                    size
                                    url
                                }
                            }
                            content
                        }
                        products_heading
                        products_intro
                        products_list {
                            product {
                                ... on PRISMIC_Product {
                                    name
                                    logo
                                    logline
                                    short_description
                                    preview_image
                                    preview_hover_image
                                    _meta {
                                        uid
                                    }
                                }
                            }
                        }
                        solutions_intro
                        solutions_heading
                        solutions {
                            destination
                            image
                            illustration {
                                ... on PRISMIC__FileLink {
                                    _linkType
                                    name
                                    size
                                    url
                                }
                            }
                            name
                            text
                        }
                        trapdoor_heading
                        trapdoor_destination
                        trapdoor_button_text
                        partners_intro
                        partners_heading
                        case_study_delay
                        case_study_list {
                            study {
                                ... on PRISMIC_Case_study {
                                    company
                                    logo
                                    quote
                                    content
                                    image
                                    statistics {
                                        label
                                        value
                                    }
                                }
                            }
                        }
                        latest_partners {
                            logo
                        }
                        events_heading
                        events_intro
                        events_slider_delay
                        events_slides {
                            heading
                            content
                            image
                            button_text
                            destination {
                                _linkType
                                ... on PRISMIC_Event {
                                    _linkType
                                    _meta {
                                        uid
                                        type
                                    }
                                }
                                ... on PRISMIC__ExternalLink {
                                    _linkType
                                    url
                                }
                            }
                        }
                        social_title
                        social_description
                        social_image
                        resources_caption
                        resources_heading
                        resources_intro
                        resources_widget_name
                    }
                }
            }
        }
    }
`;
